import { inject, Injector } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Capacitor } from '@capacitor/core';
import {
  DashboardService,
  MlaAuthService,
  OriginationTokenRefreshService,
  WorkflowStateService,
} from '@ld.consumer-portal/data-access';
import { Environment, ENVIRONMENT } from '@ld.consumer-portal/models';
import {
  AnalyticsService,
  AuthService,
  BiometricService,
  CiamAuthService,
  LoggingService,
  ServisBotService,
  TourGuideService,
  TourPreferenceService,
  UserIdleService,
} from '@ld.consumer-portal/utils';

export const signOutGuard: CanActivateFn = async () => {
  const injector = inject(Injector);
  const router = inject(Router);
  const authService = inject(AuthService);
  const userIdleService = inject(UserIdleService);
  const analyticsService = inject(AnalyticsService);
  const servisBotService = inject(ServisBotService);
  const dashboardService = inject(DashboardService);
  const mlaAuthService = inject(MlaAuthService);
  const originationTokenRefreshService = inject(OriginationTokenRefreshService);
  const workflowStateService = inject(WorkflowStateService);
  const env = inject(ENVIRONMENT);
  const logger = inject(LoggingService);
  const ciamAuthService = inject(CiamAuthService);
  const biometricService = inject(BiometricService);
  const tourPreferenceService = inject(TourPreferenceService);
  const tourGuideService = inject(TourGuideService);

  biometricService.biometricAutoLoginDone = true;
  tourGuideService.forceStop();

  let msalService: MsalService | null = null;
  try {
    msalService = injector.get(MsalService, null);
  } catch {
    // MsalService not available - continue without it
  }

  try {
    await servisBotService.clear();
  } catch (error) {
    logger.logException(error, 'Failed to clear ServisBot during sign-out');
  }

  document.body.classList.remove('servisbot-enabled');

  dashboardService.clearCache();

  userIdleService.stopWatching();
  originationTokenRefreshService.stopMonitoring();

  mlaAuthService.logout();

  workflowStateService.clearWorkflowContext();

  const logoutMessage = userIdleService.logoutMessage;

  authService.clearAllTokens();

  sessionStorage.clear();
  if (logoutMessage) {
    sessionStorage.setItem('LOGOUT_MSG_KEY', logoutMessage);
  }

  clearUserLocalStorage();
  tourPreferenceService.resetPreferences();

  localStorage.removeItem('mock_mfa_remember_device');

  analyticsService.trackLogout();

  if (env.appSettings.useCustomerIdentityAuth) {
    clearCiamState(ciamAuthService, logger);
    try {
      await ciamAuthService.logout();
    } catch (error) {
      logger.logException(error, '[signOutGuard] Failed to logout from CIAM server-side session');
    }
  }

  if (msalService) {
    clearMsalAccounts(msalService, logger);
  }

  redirectToLogout(env, router);

  return false;
};

function clearUserLocalStorage(): void {
  const keysToRemove = [
    'loanNumber',
    'helocLoanNumber',
    'isHeloc',
    'selectedLoanNumber',
    'tabWatch',
    'PaperlessDisplayStatus',
    'IsReviewPopupDesplayed',
    'ContactInfoConfirm',
    'isServisBotVisible',
    'remembered_email',
    'LoansNotEligibleForPayoffRequest',
    'loansRequestedForPayoffQuote',
    'tourPreferences',
  ];

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });
}

function redirectToLogout(env: Environment, router: Router): void {
  if (Capacitor.isNativePlatform()) {
    router.navigate(['/login']);
    return;
  }

  window.location.href = '/login';
}

function clearCiamState(ciamAuthService: CiamAuthService, logger: LoggingService): void {
  try {
    ciamAuthService.reset();
    localStorage.removeItem('ciam_session_active');
  } catch (error) {
    logger.logException(error, '[signOutGuard] Failed to clear CIAM state');
  }
}

function clearMsalAccounts(msalService: MsalService, logger: LoggingService): void {
  try {
    msalService.instance.setActiveAccount(null);

    msalService.instance.clearCache();
  } catch (error) {
    logger.logException(error, '[signOutGuard] Failed to clear MSAL accounts');
  }
}
